import dayjs, { Dayjs } from 'dayjs'

import { CreateConsultationAssessmentRequest } from 'src/api/requests'
import { ValueLabelType } from 'src/features/persons/types'

/** ICD-10 HIV disease (B20–B24) and asymptomatic HIV infection (Z21), sub-codes included, as the API checks. */
const HIV_ICD10_PREFIXES = ['B20', 'B21', 'B22', 'B23', 'B24', 'Z21']

export const isHivIcd10Code = (code?: string | null) =>
  !!code &&
  HIV_ICD10_PREFIXES.some((prefix) =>
    code.trim().toUpperCase().startsWith(prefix)
  )

/** A selected ICD-10-AM diagnosis is labelled "<code> - <name>". */
export const icd10CodeOfLabel = (label?: string) => label?.split(' - ')[0]

export const hasHivDiagnosis = (diagnoses?: ValueLabelType[]) =>
  !!diagnoses?.some((item) => isHivIcd10Code(icd10CodeOfLabel(item.label)))

/** The label of a diagnosis read from the API, in the shape the ICD-10-AM select writes. */
export const icd10Label = (code?: string | null, name?: string | null) =>
  code ? `${code} - ${name ?? ''}` : (name ?? '')

/** CDC CD4 category (field 55.2): 1 ≥ 500, 2 = 200–499, 3 < 200 cells/µl. */
export const cd4CategoryOf = (count?: number | null) => {
  if (count === undefined || count === null) return undefined
  if (count >= 500) return { category: 1, range: '≥500' }
  if (count >= 200) return { category: 2, range: '200–499' }

  return { category: 3, range: '<200' }
}

type AssessmentFormValues = {
  finalDiagnosticIds?: ValueLabelType[]
  opportunisticDiseaseIds?: ValueLabelType[]
  comments?: string
  dateOfDiseaseOnset?: Dayjs
  aidsClinicalCategoryId?: string
  aidsClinicalConditionIds?: string[]
}

/** The consultation's diagnosis (the assessment) as sent to the API; nothing when the card was left empty. */
export const assessmentToPayload = (
  assessment?: AssessmentFormValues
): CreateConsultationAssessmentRequest | undefined => {
  if (
    !Object.values(assessment ?? {}).some((value) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value)
    )
  )
    return undefined

  return {
    comments: assessment?.comments,
    finalDiagnosticIds: assessment?.finalDiagnosticIds?.map(
      (item) => item.value
    ),
    opportunisticDiseaseIds: assessment?.opportunisticDiseaseIds?.map(
      (item) => item.value
    ),
    dateOfDiseaseOnset: assessment?.dateOfDiseaseOnset
      ? dayjs(assessment.dateOfDiseaseOnset).format()
      : undefined,
    aidsClinicalCategoryId: assessment?.aidsClinicalCategoryId,
    aidsClinicalConditionIds: assessment?.aidsClinicalConditionIds?.length
      ? assessment.aidsClinicalConditionIds
      : undefined
  }
}
